/**
 * Deal-bound, versioned underwriting workbook evidence (register: Underwriting
 * Models). Backed by POST/GET /underwriting-workbooks — upload, parse, field
 * map, diff, review, promote. A workbook cell becomes an addressable
 * underwriting input only once a locked model template maps it; promoting a
 * version never projects raw cells into loan calculations. See
 * backend/app/api/v1/underwriting_workbooks.py for the authoritative contract.
 */

import api from '@/services/api'
import { toCamelCase } from '@/utils/transform'

export type WorkbookVersionStatus =
  | 'draft'
  | 'blocked'
  | 'reviewed'
  | 'promoted'
  | 'superseded'
  | 'rejected'

export type WorkbookParseStatus = 'complete' | 'blocked'
export type WorkbookMappingStatus = 'unmapped' | 'partial' | 'mapped'
export type WorkbookRole = 'underwriter' | 'sponsor' | 'other'

export type WorkbookContext = {
  role: WorkbookRole
  note: string | null
  assignedBy: string | null
  assignedAt: string | null
}

export type UnderwritingWorkbookVersion = {
  id: string
  loanId: string
  versionNumber: number
  previousVersionId: string | null
  templateId: string | null
  filename: string
  contentType: string | null
  sizeBytes: number
  contentSha256: string
  storageBackend: string
  status: WorkbookVersionStatus
  parseStatus: WorkbookParseStatus
  blockerReasons: string[]
  truncated: boolean
  mappingStatus: WorkbookMappingStatus
  mappingReviewed: boolean
  calculationSignedOff: boolean
  sheetCount: number
  importedCellCount: number
  mappedCellCount: number
  unmappedCellCount: number
  formulaCellCount: number
  missingCachedFormulaCount: number
  diffSummary: Record<string, unknown>
  uploadedBy: string
  uploadedAt: string
  reviewedBy: string | null
  reviewedAt: string | null
  reviewNote: string | null
  promotedBy: string | null
  promotedAt: string | null
  calculationProjection: string
  workbookRole?: WorkbookRole
  workbookContext?: WorkbookContext | null
}

export type UnderwritingWorkbookField = {
  fieldKey: string
  semanticKey: string | null
  mapped: boolean
  sheetName: string
  sheetIndex: number
  cellRef: string
  rowIndex: number
  columnIndex: number
  label: string | null
  formula: string | null
  rawValue: unknown
  cachedValue: unknown
  valueKind: string
  hasCachedValue: boolean
}

export type UnderwritingWorkbookFieldChange = {
  comparisonKey: string
  fieldKeyBefore: string | null
  fieldKeyAfter: string | null
  semanticKeyBefore: string | null
  semanticKeyAfter: string | null
  changeTypes: string[]
  formulaBefore: string | null
  formulaAfter: string | null
  valueBefore: unknown
  valueAfter: unknown
  mappedBefore: boolean | null
  mappedAfter: boolean | null
}

export type WorkbookFieldsPage = {
  total: number
  offset: number
  limit: number
  items: UnderwritingWorkbookField[]
}

export type WorkbookDiffPage = {
  previousVersionId: string | null
  summary: Record<string, unknown>
  total: number
  offset: number
  limit: number
  items: UnderwritingWorkbookFieldChange[]
}

export type ReviewDecisionBody = {
  decision: 'reviewed' | 'rejected'
  note: string
  confirmClientMapping?: boolean
  confirmCalculationSignoff?: boolean
  mappingBasis?: string
  calculationBasis?: string
}

export type WorkbookUploadOptions = {
  templateId?: string
  role?: WorkbookRole
  contextNote?: string
}

export type ComparisonFieldProvenance = {
  versionId: string
  filename: string
  role: WorkbookRole
  sheetName: string
  sheetIndex: number
  cellRef: string
  rowIndex: number
  columnIndex: number
}

export type ComparisonFieldCandidate = {
  fieldId: string
  fieldKey: string
  semanticKey: string
  label: string | null
  value: unknown
  valueSource: 'raw' | 'cached'
  rawValue: unknown
  cachedValue: unknown
  valueKind: string
  formula: string | null
  sheetName: string
  sheetIndex: number
  cellRef: string
  rowIndex: number
  columnIndex: number
  provenance: ComparisonFieldProvenance
}

export type GoverningDecision = {
  id: string
  loanId: string
  semanticKey: string
  underwriterVersionId: string
  sponsorVersionId: string
  governingSide: 'underwriter' | 'sponsor'
  governingVersionId: string
  governingFieldId: string
  reason: string
  decidedBy: string
  decidedAt: string
  supersedesDecisionId: string | null
}

export type ComparisonRowStatus =
  | 'matching'
  | 'different'
  | 'underwriter_only'
  | 'sponsor_only'
  | 'ambiguous'

export type UnderwritingComparisonRow = {
  semanticKey: string
  status: ComparisonRowStatus | string
  underwriter: ComparisonFieldCandidate | null
  sponsor: ComparisonFieldCandidate | null
  underwriterCandidates: ComparisonFieldCandidate[]
  sponsorCandidates: ComparisonFieldCandidate[]
  governingDecision?: GoverningDecision | null
}

export type UnderwritingComparisonVersion = {
  id: string
  versionNumber: number
  filename: string
  role: 'underwriter' | 'sponsor'
}

export type UnderwritingComparison = {
  loanId: string
  underwriter: UnderwritingComparisonVersion
  sponsor: UnderwritingComparisonVersion
  summary: {
    total: number
    matching: number
    different: number
    underwriterOnly: number
    sponsorOnly: number
    ambiguous: number
    governed: number
  }
  rows: UnderwritingComparisonRow[]
}

export type GoverningDecisionBody = {
  underwriterVersionId: string
  sponsorVersionId: string
  semanticKey: string
  governingVersionId: string
  governingFieldId?: string | null
  reason: string
}

/** Mirrors the server's own ceiling (MAX_UPLOAD_BYTES in underwriting_workbooks.py). */
export const MAX_WORKBOOK_UPLOAD_BYTES = 50 * 1024 * 1024
export const WORKBOOK_FIELD_PAGE_LIMIT = 5000

function reviewBodyToSnake(body: ReviewDecisionBody) {
  return {
    decision: body.decision,
    note: body.note,
    confirm_client_mapping: body.confirmClientMapping ?? false,
    confirm_calculation_signoff: body.confirmCalculationSignoff ?? false,
    mapping_basis: body.mappingBasis ?? null,
    calculation_basis: body.calculationBasis ?? null,
  }
}

/** What a mapping sweep found, and what it could not place. */
export interface MappingProposal {
  templateId: string
  templateStatus: string
  /** Metrics anchored confidently enough to propose. */
  recommended: number
  /** Metrics seen but too ambiguous to anchor without a person. */
  heldForReview: number
  mappedCells: number
  importedCells: number
  metricsSeen?: number
  held?: Array<Record<string, unknown>>
}

/** One cell a workbook offers for a metric mapgen would not place alone. */
export interface HeldMetricCandidate {
  sheet: string
  labelCell: string
  valueCell: string
  label: string | null
  preview: string | null
  /** The label matched the lexicon exactly, rather than merely containing it. */
  exact: boolean
  /** What the deal field would hold, or null with `refusal` saying why not. */
  value: number | null
  refusal: string | null
}

/** A metric the model states ambiguously, with the cells in question. */
export interface HeldMetric {
  metric: string
  /** The catalogue field this would answer; null when none holds this metric. */
  field: string | null
  fieldLabel: string | null
  unit: string | null
  candidates: HeldMetricCandidate[]
}

/** What placing one metric by hand actually did. */
export interface HeldMetricResolution {
  metric: string
  field: string | null
  cell: string
  mappedCells: number
  fieldsFilled: string[]
  /** False when the cell anchored but no deal field took a value from it. */
  valueLanded: boolean
  remaining: number
}

/** One deal field a model states, beside what the deal holds. */
export type ModelDealViewRow = {
  field: string
  label: string
  kind: string
  unit: string | null
  modelValue: number
  provenance: {
    filename: string
    sheet: string | null
    cell: string | null
    label: string | null
    computed: boolean
    versionNumber: number | null
  }
  alternatives: { value: number; sheet: string | null; cell: string | null }[]
  dealValue: number | string | null
  /** The deal's value was taken from this very model — the model's own number, not a rival. */
  dealFromThisModel: boolean
  delta: { absolute: number; relative: number | null; agrees: boolean } | null
  adoptable: boolean
}

export type ModelDealView = {
  versionId: string
  loanId: string
  filename: string
  versionNumber: number
  role: WorkbookRole
  parseStatus: string
  templateId: string | null
  sections: { id: string; label: string; rows: ModelDealViewRow[] }[]
  /** Mapped metrics the deal record has no column for — the first sixty. */
  unmatched: { semanticKey: string; label: string | null; sheet: string | null; cell: string | null; value: unknown }[]
  /** Every such metric, listed or not (UI-12). Absent from an older server. */
  unmatchedTotal?: number
  coverage: { fieldsStated: number; fieldsApplicable: number; cellsMapped: number; cellsImported: number }
}

export const underwritingWorkbooksService = {
  /** What this model states about the deal, field by field, beside the deal. */
  dealView: (versionId: string) =>
    api
      .get(`/underwriting-workbooks/${versionId}/deal-view`)
      .then((r) => toCamelCase<ModelDealView>(r.data)),

  /** Put one of the model's figures onto the deal, the cell in its lineage. */
  adoptFigure: (versionId: string, body: { field: string; reason?: string }) =>
    api
      .post(`/underwriting-workbooks/${versionId}/adopt`, body)
      .then((r) => toCamelCase<{ field: string; value: number; previous: unknown; source: string }>(r.data)),

  listForLoan: (loanId: string) =>
    api
      .get(`/underwriting-workbooks/loans/${loanId}`)
      .then((r) => toCamelCase<UnderwritingWorkbookVersion[]>(r.data)),

  upload: (loanId: string, file: File, options?: WorkbookUploadOptions) => {
    const form = new FormData()
    form.append('file', file)
    if (options?.templateId) form.append('template_id', options.templateId)
    if (options?.role) form.append('workbook_role', options.role)
    if (options?.contextNote) form.append('context_note', options.contextNote)
    return api
      .post(`/underwriting-workbooks/loans/${loanId}`, form)
      .then((r) => toCamelCase<UnderwritingWorkbookVersion>(r.data))
  },

  get: (versionId: string) =>
    api
      .get(`/underwriting-workbooks/${versionId}`)
      .then((r) => toCamelCase<UnderwritingWorkbookVersion>(r.data)),

  setContext: (versionId: string, context: { role: WorkbookRole; note?: string }) =>
    api
      .put(`/underwriting-workbooks/${versionId}/context`, {
        role: context.role,
        note: context.note ?? null,
      })
      .then((r) => toCamelCase<UnderwritingWorkbookVersion>(r.data)),

  comparison: (underwriterVersionId: string, sponsorVersionId: string) =>
    api
      .get('/underwriting-workbooks/comparisons', {
        params: {
          underwriter_version_id: underwriterVersionId,
          sponsor_version_id: sponsorVersionId,
        },
      })
      .then((r) => toCamelCase<UnderwritingComparison>(r.data)),

  setGoverningDecision: (body: GoverningDecisionBody) =>
    api
      .post('/underwriting-workbooks/comparisons/governing-decisions', {
        underwriter_version_id: body.underwriterVersionId,
        sponsor_version_id: body.sponsorVersionId,
        semantic_key: body.semanticKey,
        governing_version_id: body.governingVersionId,
        governing_field_id: body.governingFieldId ?? null,
        reason: body.reason,
      })
      .then((r) => toCamelCase<GoverningDecision>(r.data)),

  /**
   * Sweep an unmapped workbook for anchors and land them as a draft map.
   *
   * The map governs nothing on its own — it still has to go through
   * submit -> approve -> lock in the template registry before a review can
   * cite it. This only makes there be something to take through that chain.
   */
  proposeMapping: (versionId: string) =>
    api
      .post(`/underwriting-workbooks/${versionId}/propose-mapping`)
      .then((r) => toCamelCase<MappingProposal>(r.data)),

  /**
   * The metrics this workbook states ambiguously, with the cells in question.
   *
   * mapgen places a metric only when the evidence points at one cell and holds
   * the rest — the right call for a machine, since a wrong NOI cell reads as a
   * real number for ever. This is how a person answers the ones it held.
   */
  heldMetrics: (versionId: string) =>
    api
      .get(`/underwriting-workbooks/${versionId}/held-metrics`)
      .then((r) => toCamelCase<{ items: HeldMetric[] }>(r.data).items),

  /** Anchor one held metric to a chosen cell, and fill the deal from it. */
  resolveHeldMetric: (
    versionId: string,
    metric: string,
    cell: { sheet: string; labelCell: string; valueCell: string },
  ) =>
    api
      .post(
        `/underwriting-workbooks/${versionId}/held-metrics/${encodeURIComponent(metric)}/resolve`,
        { sheet: cell.sheet, label_cell: cell.labelCell, value_cell: cell.valueCell },
      )
      .then((r) => toCamelCase<HeldMetricResolution>(r.data)),

  fields: (
    versionId: string,
    params?: {
      sheet?: string
      semanticKey?: string
      mapped?: boolean
      search?: string
      offset?: number
      limit?: number
    },
  ) =>
    api
      .get(`/underwriting-workbooks/${versionId}/fields`, {
        params: {
          sheet: params?.sheet,
          semantic_key: params?.semanticKey,
          mapped: params?.mapped,
          search: params?.search,
          offset: params?.offset,
          limit: params?.limit ?? WORKBOOK_FIELD_PAGE_LIMIT,
        },
      })
      .then((r) => toCamelCase<WorkbookFieldsPage>(r.data)),

  diff: (versionId: string, params?: { offset?: number; limit?: number }) =>
    api
      .get(`/underwriting-workbooks/${versionId}/diff`, { params })
      .then((r) => toCamelCase<WorkbookDiffPage>(r.data)),

  review: (versionId: string, body: ReviewDecisionBody) =>
    api
      .post(`/underwriting-workbooks/${versionId}/review`, reviewBodyToSnake(body))
      .then((r) => toCamelCase<UnderwritingWorkbookVersion>(r.data)),

  promote: (versionId: string) =>
    api
      .post(`/underwriting-workbooks/${versionId}/promote`)
      .then((r) => toCamelCase<UnderwritingWorkbookVersion>(r.data)),

  downloadExport: (versionId: string) =>
    api
      .get(`/underwriting-workbooks/${versionId}/export`, { responseType: 'blob' })
      .then((r) => r.data as Blob),

  downloadSource: (versionId: string) =>
    api
      .get(`/underwriting-workbooks/${versionId}/source`, { responseType: 'blob' })
      .then((r) => r.data as Blob),
}
